/**
 * V2 Demo Page — public /demo/{token}
 *
 * Michael's marketing engine. Personalised demo Leak Check + live "Ask
 * Kindra anything" chat. Non-negotiable honest-framing banner at the top
 * so the demo never claims to analyse the prospect's actual business.
 *
 * Data comes from /api/demo/{token} (grounded in industry datasets +
 * kindra_brain). Chat calls /api/demo/{token}/chat with hard rate limits.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = '#100838';
const ICY = '#DCF0F5';
const MUTED = 'rgba(220,240,245,0.72)';
const FAINT = 'rgba(220,240,245,0.50)';
const HAIR = 'rgba(220,240,245,0.14)';
const TEAL = '#5FE0BE';
const MAGENTA = '#EB258D';
const AMBER = '#F5C268';

export default function V2DemoPage() {
  const { token } = useParams();
  const [demo, setDemo] = useState(null);
  const [err, setErr] = useState('');
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    // Runtime noindex (nginx also adds X-Robots-Tag header)
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch (_) {} };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/demo/${token}`);
        if (!cancelled) setDemo(r.data);
      } catch (e) { if (!cancelled) setErr(e?.response?.data?.detail || 'This demo is unavailable.'); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (err) return <FullscreenMsg text={err} />;
  if (!demo) return <FullscreenMsg text="Loading your demo..." />;

  return (
    <div style={{ background: NAVY, color: ICY, minHeight: '100vh', paddingBottom: 120 }} data-testid="demo-page">
      {/* Honest-framing banner — MUST stay top */}
      <div style={{ background: 'rgba(31,182,193,0.10)', borderBottom: `1px solid ${HAIR}`, padding: '10px 20px' }}
           data-testid="demo-banner">
        <div style={{ maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter', fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}>
          {demo.banner}
        </div>
      </div>

      {/* Hero */}
      <section style={{ padding: '48px 20px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontFamily: 'Inter', color: FAINT, fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 10 }}>
            Demo · {demo.industry_name}
          </p>
          <h1 data-testid="demo-hero" style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(28px,4vw,44px)', lineHeight: 1.15, marginBottom: 14, color: ICY }}>
            {demo.hero_headline}
          </h1>
          <p style={{ fontFamily: 'Inter', fontSize: 16, lineHeight: 1.6, color: MUTED, maxWidth: 720 }}>
            {demo.hero_subline}
          </p>
        </div>
      </section>

      {/* Journey / Leak Map */}
      <section style={{ padding: '20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 20, marginBottom: 14, color: ICY }}>Where {demo.industry_name} businesses leak</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }} data-testid="demo-leak-map">
            {demo.leak_map.map((s, i) => (
              <div key={i} style={{ background: '#1A0F44', border: `1px solid ${HAIR}`,
                                    borderLeft: `3px solid ${s.status === 'leaking' ? MAGENTA : TEAL}`,
                                    borderRadius: 10, padding: '12px 14px' }}>
                <p style={{ fontFamily: 'Inter', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                            color: s.status === 'leaking' ? MAGENTA : TEAL, fontWeight: 700, marginBottom: 6 }}>
                  {s.status}
                </p>
                <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, marginBottom: s.note ? 6 : 0 }}>{s.stage}</p>
                {s.note && <p style={{ fontFamily: 'Inter', fontSize: 12, color: MUTED, lineHeight: 1.5 }}>{s.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leak cards */}
      <section style={{ padding: '20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 20, marginBottom: 14 }}>What we'd fix for you</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }} data-testid="demo-leak-cards">
            {demo.leak_cards.map((c, i) => (
              <div key={i} style={{ background: '#1A0F44', border: `1px solid ${HAIR}`, borderRadius: 12, padding: '18px 20px' }}>
                <p style={{ fontFamily: 'Inter', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                            color: AMBER, fontWeight: 700, marginBottom: 8 }}>{c.leak_area}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: ICY, lineHeight: 1.55, marginBottom: 12 }}>{c.pain}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 14, color: MUTED, lineHeight: 1.55, marginBottom: 10 }}>
                  <b style={{ color: TEAL }}>What we'd build:</b> {c.what_wed_build}
                </p>
                <p style={{ fontFamily: 'Inter', fontSize: 12, color: FAINT, lineHeight: 1.5, fontStyle: 'italic' }}>{c.and_youd_own_it}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benchmarks — you vs industry */}
      <section style={{ padding: '20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 20, marginBottom: 14, color: ICY }}>Industry benchmarks</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }} data-testid="demo-benchmarks">
            {demo.benchmarks.map((b, i) => (
              <div key={i} style={{ background: '#1A0F44', border: `1px solid ${HAIR}`, borderRadius: 12, padding: '18px 20px' }}>
                <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 15, marginBottom: 10 }}>{b.label}</p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'baseline' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 10.5, color: MAGENTA, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>Industry norm</p>
                    <p style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 28, color: MAGENTA }}>{b.industry_norm}<span style={{ fontSize: 13, color: MUTED, fontWeight: 500, marginLeft: 4 }}>{b.unit}</span></p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 10.5, color: TEAL, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>Healthy target</p>
                    <p style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 28, color: TEAL }}>{b.healthy}<span style={{ fontSize: 13, color: MUTED, fontWeight: 500, marginLeft: 4 }}>{b.unit}</span></p>
                  </div>
                </div>
                <p style={{ fontFamily: 'Inter', fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{b.reader}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locked deep-dive */}
      <section style={{ padding: '20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div data-testid="demo-locked" style={{ position: 'relative', background: '#1A0F44', border: `1px solid ${HAIR}`, borderRadius: 14, padding: '32px 24px', overflow: 'hidden' }}>
            <p style={{ fontFamily: 'Inter', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: AMBER, fontWeight: 700, marginBottom: 10 }}>
              🔒 Deep dive — locked
            </p>
            <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, marginBottom: 14 }}>
              This part gets built from YOUR numbers.
            </p>
            <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: MUTED, lineHeight: 1.6, filter: 'blur(3px)', userSelect: 'none' }}>
              {demo.locked_teaser}
            </p>
            <p style={{ fontFamily: 'Inter', fontSize: 14.5, color: MUTED, lineHeight: 1.6, filter: 'blur(3px)', userSelect: 'none', marginTop: 8 }}>
              ▓▓▓▓▓ ▓▓▓▓▓▓▓▓▓ ▓▓▓ ▓▓▓▓▓▓ · ▓▓▓▓▓▓▓ ▓▓▓▓▓ ▓▓ ▓▓▓▓▓ · ▓▓▓▓ ▓▓▓▓▓ ▓▓ ▓▓▓ ▓▓▓▓▓▓▓▓▓▓▓
            </p>
            <div style={{ marginTop: 18 }}>
              <Link to="/book?src=demo" data-testid="demo-cta-book"
                    style={{ display: 'inline-block', padding: '14px 26px', borderRadius: 999,
                              background: 'linear-gradient(95deg, #EB258D 0%, #A95CD6 100%)', color: '#fff',
                              fontFamily: 'Inter', fontWeight: 700, textDecoration: 'none', fontSize: 15 }}>
                Book the free 30-min Leak Check →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Chat launcher — sticky on mobile, opens the panel */}
      <button data-testid="demo-chat-launcher"
              onClick={() => setChatOpen((v) => !v)}
              style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 40,
                        background: 'linear-gradient(95deg, #1FB6C1, #5FE0BE)', color: '#0d0530',
                        border: 'none', borderRadius: 999, padding: '14px 20px',
                        fontFamily: 'Inter', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        boxShadow: '0 20px 40px -12px rgba(31,182,193,0.55)' }}>
        {chatOpen ? '× Close chat' : '💬 Ask Kindra anything'}
      </button>

      {chatOpen && <DemoChat token={token} firstName={demo.first_name} businessName={demo.business_name} onClose={() => setChatOpen(false)} />}
    </div>
  );
}

function FullscreenMsg({ text }) {
  return (
    <div style={{ background: NAVY, color: ICY, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter' }}>
      {text}
    </div>
  );
}

function DemoChat({ token, firstName, businessName, onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: `G'day ${firstName || 'there'} — ask me anything about what you're seeing.` },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ctaUrl, setCtaUrl] = useState(null);
  const [capped, setCapped] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending || capped) return;
    const msg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/demo/${token}/chat`, { message: msg });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
      if (r.data.cta_url) setCtaUrl(r.data.cta_url);
      if (r.data.capped) setCapped(true);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: 'The chat had a wobble — book the Leak Check and I\'ll cover it all on the call.' }]);
    } finally { setSending(false); }
  };

  return (
    <div data-testid="demo-chat-panel"
         style={{ position: 'fixed', right: 20, bottom: 90, width: 'min(96vw, 420px)', maxHeight: '72vh',
                  background: '#12083b', border: `1px solid ${HAIR}`, borderRadius: 14, zIndex: 41,
                  display: 'flex', flexDirection: 'column', boxShadow: '0 30px 60px -20px rgba(0,0,0,.55)' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${HAIR}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, color: ICY }}>Ask Kindra</p>
          <p style={{ fontFamily: 'Inter', fontSize: 11, color: FAINT }}>About {businessName}'s demo</p>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', color: MUTED, border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
      </div>
      <div ref={scrollRef} data-testid="demo-chat-messages"
           style={{ padding: 14, overflowY: 'auto', flex: 1, minHeight: 240 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
              background: m.role === 'user' ? 'rgba(52,209,176,0.16)' : 'rgba(220,240,245,0.06)',
              color: ICY, fontFamily: 'Inter', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
            }}>{m.text}</div>
          </div>
        ))}
        {ctaUrl && (
          <div style={{ marginTop: 8 }}>
            <Link to={ctaUrl} data-testid="demo-chat-cta"
                  style={{ display: 'inline-block', background: 'linear-gradient(95deg, #EB258D, #A95CD6)', color: '#fff',
                            padding: '10px 18px', borderRadius: 999, textDecoration: 'none', fontFamily: 'Inter', fontWeight: 700, fontSize: 13 }}>
              Book the Free Leak Check →
            </Link>
          </div>
        )}
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${HAIR}` }}>
        {capped ? (
          <p style={{ fontFamily: 'Inter', fontSize: 12, color: FAINT, textAlign: 'center' }}>Session capped — book the call to keep going.</p>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              data-testid="demo-chat-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Ask a question…"
              disabled={sending}
              style={{ flex: 1, background: 'rgba(220,240,245,0.06)', border: `1px solid ${HAIR}`, borderRadius: 10,
                        padding: '10px 12px', color: ICY, fontFamily: 'Inter', fontSize: 14 }} />
            <button data-testid="demo-chat-send" onClick={send} disabled={sending || !input.trim()}
                    style={{ background: TEAL, color: NAVY, border: 'none', borderRadius: 10, padding: '10px 16px', fontFamily: 'Inter', fontWeight: 700, cursor: 'pointer', opacity: sending ? 0.5 : 1 }}>
              {sending ? '…' : 'Send'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
